import { useLazyGetFirmBillUploadOperationReportQuery } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import { useState, useEffect } from "react";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import useReportFirms from "../../../Hooks/useReportFirms";
import moment from "moment-timezone";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import { FirmDropdown, CustomDropdown } from "../../../Components/atoms";
import { buildExcelConfig } from "../../../Components/Reports/reportExcelDefaults";
import { getMonthStart } from "../../../utils/reportDateDefaults";

const billTypeOptions = [
  { id: "all", value: "", label: "Tümü" },
  { id: "Bill", value: "Bill", label: "Fatura" },
  { id: "FreeUpload", value: "FreeUpload", label: "Serbest Yükleme" },
  { id: "GetReturn", value: "GetReturn", label: "İade" },
  { id: "QuotaShare", value: "QuotaShare", label: "Kota Paylaşımı / Satışı" },
];

// Ürün tipleri seçenekleri
const productTypeOptions = [
  { id: "all", label: "Tümü", value: "" },
  { id: "cay", label: "Çay", value: "Çay" },
  { id: "turk-kahvesi", label: "Türk Kahvesi", value: "Türk Kahvesi" },
  { id: "filtre-kahve", label: "Filtre Kahve", value: "Filtre Kahve" },
];

export default function ReportFirmUploadedBillPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const getEndOfDay = (date = new Date()) => {
    const todayEnd = moment(date).tz(tz).endOf("day");
    return todayEnd.toDate();
  };

  // Başlangıç varsayılanı ayın ilk günü (Workspace #154)
  const [startDate, setStartDate] = useState(() => getMonthStart(tz));
  const [endDate, setEndDate] = useState(getEndOfDay());
  const [billNo, setBillNo] = useState("");
  const [firmName, setFirmName] = useState("");
  const [productTypeName, setProductTypeName] = useState("");

  const [billType, setBillType] = useState("");
  const [parsedData, setParsedData] = useState([]);

  const { firms, isLoading: firmDataIsLoading } = useReportFirms(auth.accessToken);

  const handleFirmChange = (firm) => {
    if (!firm || firm._id === "all") {
      setFirmName({ _id: "all", firmName: "Tüm Firmalar" });
    } else {
      setFirmName(firm);
    }
  };

  const [getBillReport, { data: billReport, isLoading, isFetching, error }] =
    useLazyGetFirmBillUploadOperationReportQuery();

  // RTK Query'de `isLoading` YALNIZCA ilk yüklemede true olur; aynı sayfada
  // "Raporu Getir"e tekrar basınca istek gitse bile loading görünmüyordu.
  // Bekleme durumu için `isFetching` de hesaba katılır.
  const isBusy = isLoading || isFetching;

  useEffect(() => {
    if (billReport && billReport.data) {
      setParsedData(billReport.data);
    }
  }, [billReport]);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      return;
    }
    getBillReport({
      token,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      billNo: billNo || "undefined",
      firmName: firmName || "undefined",
      productTypeName: productTypeName || "undefined",
      billType: billType || "undefined",
      timeZone: tz,
    });
  };

  const handleClearFilters = () => {
    setStartDate(getMonthStart(tz));
    setEndDate(getEndOfDay());
    setBillNo("");
    setFirmName("");
    setProductTypeName("");
    setBillType("");
    setParsedData([]);
  };

  // Dosya adı oluşturma
  const fileName = `Yuklenen_Faturalar_${
    startDate ? startDate.toISOString().split("T")[0] : ""
  }_${endDate ? endDate.toISOString().split("T")[0] : ""}.xlsx`;

  const getBillTypeLabel = (value) => {
    return (
      billTypeOptions.find((option) => option.value === value)?.label || value
    );
  };



  // Excel verilerini hazırlama
  const formatExcelData = (data) => {
    if (!data) return [];
    return data.map((item) => ({
      "İşlemi Yapan": item.billerName || "-",
      "Fatura Numarası": item.invoiceNumber || "-",
      "Fatura Tipi": getBillTypeLabel(item.invoiceType),
      "Firma Adı": item.firmName || "-",
      "Bayser No": item.bayserNo || "-",
      "Ürün Tipi": item.productType || "-",
      "Ürün Adı": item.productName || "-",
      Kota: item.quota || "-",
      Fiyat: item.price || "-",
      Gelir: item.income || "-",
      Tarih: item.date
        ? moment(item.date).tz(tz).format("DD/MM/YYYY HH:mm")
        : "-",
    }));
  };

  // Excel konfigürasyonu
  const excelConfig = buildExcelConfig({
    sheetName: "Yüklenen Faturalar Raporu",
    widths: [20, 18, 15, 25, 16, 15, 20, 10, 12, 12, 20],
    numericColumns: [],
    columnMap: {
      billerName: 0,
      invoiceNumber: 1,
      invoiceType: 2,
      firmName: 3,
      bayserNo: 4,
      productType: 5,
      productName: 6,
      quota: 7,
      price: 8,
      income: 9,
      date: 10,
    },
  });

  const pdfBase64 = billReport?.pdfBase64;

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, billReport?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-semibold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <ReportLayout
        title="Yüklenen Faturalar Raporu"
      headerActions={headerActions}
        fileName={fileName}
        excelData={formatExcelData(parsedData)}
        excelConfig={excelConfig}
        sidebar={
          <ReportFilterCard
          title="Filtre Seçenekleri"
          description="Rapor için filtreleri seçin"
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isBusy}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          >
          <div className="grid grid-cols-1 gap-4">
          {/* Fatura Numarası */}
          <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
          Fatura Numarası
          </label>
          <input
          type="text"
          placeholder="Fatura numarasını girin"
          value={billNo}
          onChange={(e) => setBillNo(e.target.value)}
          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 shadow-sm hover:shadow-md"
          />
          </div>

          {/* Firma */}
          <FirmDropdown
          value={firmName}
          onChange={handleFirmChange}
          firms={firms}
          loading={firmDataIsLoading}
          />

          {/* Ürün Tipi */}
          <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
          Ürün Tipi
          </label>
          <CustomDropdown
          value={productTypeOptions.find(
          (opt) => opt.value === productTypeName
          )}
          onChange={(option) => setProductTypeName(option?.value || "")}
          options={productTypeOptions}
          placeholder="Ürün tipi seçin..."
          showSearch={false}
          />
          </div>

          {/* Fatura Tipi */}
          <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
          Fatura Tipi
          </label>
          <CustomDropdown
          value={billTypeOptions.find((opt) => opt.value === billType)}
          onChange={(option) => setBillType(option?.value || "")}
          options={billTypeOptions}
          placeholder="Tümü"
          showSearch={false}
          />
          </div>
          </div>
          </ReportFilterCard>
        }
      >
        {/* Loading State */}
        {isBusy && (
          <ReportLoadingState
            title="Fatura Raporu Hazırlanıyor"
            description="Veriler yükleniyor, lütfen bekleyin..."
            progressText="Veriler işleniyor..."
            skeletonRows={5}
            skeletonColumns={8}
          />
        )}

        {/* Error State */}
        {error && (
          <ReportErrorState
            title="Hata Oluştu"
            message={error.message || "Rapor yüklenirken bir hata oluştu."}
            icon="warning"
          />
        )}

        {/* PDF önizleme — rapor sunucuda tasarlanmış PDF olarak üretilir */}
        {!isBusy && !error && pdfBase64 && (
          <ReportPdfPreview
            pdfBase64={pdfBase64}
            title={`Yüklenen Faturalar Raporu — ${parsedData.length} fatura`}
          />
        )}

        {/* Empty State */}
        {!isBusy && !error && !pdfBase64 && (
          <ReportEmptyState
            title="Veri Bulunamadı"
            message="Seçilen filtrelerde fatura bulunamadı."
            icon="document"
          />
        )}
      </ReportLayout>
    </LocalizationProvider>
  );
}
